<template>
    <div class="cgraph-wrap" :class="{ 'cgraph-fullscreen': isFullscreen }">
        <!-- Toolbar -->
        <div class="cgraph-toolbar" v-if="hasGraph">
            <div class="cgraph-tb-left">
                <span class="cgraph-stats">
                    <i class="fa fa-circle-nodes me-1 opacity-50"></i>
                    {{ graphStats.nodes }} nodes
                    <span class="cgraph-stats-sep">·</span>
                    {{ graphStats.edges }} edges
                    <template v-if="showBridgeStats && graphStats.sharedEntities">
                        <span class="cgraph-stats-sep">·</span>
                        <span class="cgraph-stats-shared">{{ graphStats.sharedEntities }} shared</span>
                    </template>
                    <template v-if="connectingCount > 0">
                        <span class="cgraph-stats-sep">·</span>
                        <span class="cgraph-stats-connecting" title="Entities that connect the two clusters">
                            {{ connectingCount }} connecting
                        </span>
                    </template>
                    <template v-if="clusterEdgeCount > 0">
                        <span class="cgraph-stats-sep">·</span>
                        <span
                            class="cgraph-stats-cluster-edges"
                            title="Real graph edges joining the two clusters (connecting_cluster_edges)"
                        >
                            {{ clusterEdgeCount }} cluster {{ clusterEdgeCount === 1 ? 'edge' : 'edges' }}
                        </span>
                    </template>
                    <template v-if="graphStats.collapsedEntities > 0">
                        <span class="cgraph-stats-sep">·</span>
                        {{ graphStats.collapsedEntities }} collapsed
                    </template>
                </span>
            </div>
            <div class="cgraph-tb-right">
                <button class="cgraph-tb-btn" @click="zoomIn" title="Zoom in"><i class="fa fa-magnifying-glass-plus"></i></button>
                <button class="cgraph-tb-btn" @click="zoomOut" title="Zoom out"><i class="fa fa-magnifying-glass-minus"></i></button>
                <button class="cgraph-tb-btn" @click="fitToScreen" title="Fit to screen"><i class="fa fa-down-left-and-up-right-to-center"></i></button>
                <button class="cgraph-tb-btn" @click="resetView" title="Reset"><i class="fa fa-rotate-left"></i></button>
                <div class="cgraph-tb-sep"></div>
                <button class="cgraph-tb-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'">
                    <i :class="isFullscreen ? 'fa fa-compress' : 'fa fa-expand'"></i>
                </button>
            </div>
        </div>

        <!-- Legend -->
        <div
            class="cgraph-legend"
            :class="{ 'is-wrapped': legendExpanded }"
            v-if="hasGraph && legendItems.length"
        >
            <template v-for="item in visibleLegendItems" :key="item.type">
                <button
                    v-if="!item.legendOverflow"
                    type="button"
                    class="cgraph-legend-chip"
                    :class="{ 'is-off': hiddenTypes.has(item.type) }"
                    :title="item.full || item.label"
                    @click="toggleType(item.type)"
                >
                    <span class="cgraph-legend-dot" :style="{ background: hiddenTypes.has(item.type) ? '#bbb' : item.color }"></span>
                    <span class="cgraph-legend-label">{{ item.label }}</span>
                    <span class="cgraph-legend-cnt">{{ item.count }}</span>
                </button>
                <button v-else type="button" class="cgraph-legend-chip cgraph-legend-more" @click="legendExpanded = !legendExpanded">
                    <span class="cgraph-legend-label">{{ item.label }}</span>
                    <i :class="legendExpanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="font-size:8px;margin-left:3px;opacity:0.7"></i>
                </button>
            </template>
        </div>

        <!-- SVG canvas -->
        <!-- Canvas area: inspector floats over SVG, never shifts layout -->
        <div class="cgraph-canvas-area">
            <transition name="cgraph-insp">
                <div
                    v-if="hasGraph && graphInspector.visible"
                    class="cgraph-inspector"
                    role="status"
                    aria-live="polite"
                >
                    <span class="cgraph-insp-chip" :style="{ '--ic': graphInspector.color }">
                        <span class="cgraph-insp-dot"></span>
                        <span class="cgraph-insp-label">{{ graphInspector.fullLabel }}</span>
                    </span>
                    <span class="cgraph-insp-meta">
                        <template v-for="(ln, i) in graphInspector.lines" :key="i">
                            <span v-if="i > 0" class="cgraph-inspector-sep">·</span>{{ ln }}
                        </template>
                    </span>
                </div>
            </transition>
            <div v-if="hasGraph && laneHeaders.length === 2" class="cgraph-lane-hdrs">
                <span
                    v-for="lh in laneHeaders"
                    :key="'lh-' + lh.side"
                    class="cgraph-lane-hdr"
                    :class="'cgraph-lane-hdr-' + lh.side"
                    :style="{ '--lane-color': lh.color }"
                    :title="lh.full"
                >
                    <span class="cgraph-lane-dot"></span>
                    <span class="cgraph-lane-name">{{ lh.label }}</span>
                    <span v-if="lh.full && lh.full !== lh.label" class="cgraph-lane-id">{{ lh.full }}</span>
                </span>
            </div>
            <div ref="graphContainer" class="cgraph-canvas"></div>
        </div>

        <!-- Detail panel with slide-in transition -->
        <transition name="cgraph-slide">
            <div
                v-if="detailNode"
                class="cgraph-panel"
                :class="{
                    'cgraph-panel--wide':
                        detailNode.type === 'entity_group' ||
                        detailNode.type === 'entity_bundle' ||
                        (detailNode.clusterEdgeDetails || []).length > 0,
                }"
            >
                <div class="cgraph-panel-hdr" :style="{ '--node-color': nodeFill(detailNode) }">
                    <div class="cgraph-panel-hdr-inner">
                        <span class="cgraph-panel-icon-badge">
                            <i :class="panelIconClass(detailNode)"></i>
                        </span>
                        <span class="cgraph-panel-hdr-title">{{ panelTitle(detailNode) }}</span>
                    </div>
                    <button class="cgraph-panel-close" @click="detailNode = null" aria-label="Close"><i class="fa fa-xmark"></i></button>
                </div>
                <div class="cgraph-panel-body">
                    <!-- Entity group -->
                    <template v-if="detailNode.type === 'entity_group'">
                        <div class="cgraph-panel-meta-row">
                            <span class="cgraph-type-pill">{{ detailNode.neighborType || detailNode.key || 'type~domain' }}</span>
                            <span class="cgraph-cnt-badge">{{ entityGroupDetailIds.length }} IDs</span>
                            <span v-if="detailNode.connectingIdCount" class="cgraph-flag cgraph-flag--amber">
                                <i class="fa fa-link me-1"></i>{{ detailNode.connectingIdCount }} connecting
                            </span>
                        </div>
                        <div v-if="entityGroupDetailIds.length" class="cgraph-id-list">
                            <div v-for="(id, ii) in entityGroupDetailIds" :key="'eg-'+ii" class="cgraph-id-row">{{ id }}</div>
                        </div>
                        <p v-else class="cgraph-empty-note">No IDs parsed for this group.</p>
                        <template v-if="(detailNode.connectingEntityIds || []).length">
                            <div class="cgraph-section-lbl">Connecting entity IDs</div>
                            <div class="cgraph-id-list">
                                <div v-for="(id, ci) in detailNode.connectingEntityIds" :key="'ce-'+ci" class="cgraph-id-row">{{ id }}</div>
                            </div>
                        </template>
                    </template>

                    <!-- Entity bundle -->
                    <template v-else-if="detailNode.type === 'entity_bundle'">
                        <p class="cgraph-panel-note">Large entity lists are sampled on the graph for performance.</p>
                        <div class="cgraph-stat-grid">
                            <div class="cgraph-stat-cell">
                                <span class="cgraph-stat-lbl">Total</span>
                                <span class="cgraph-stat-val">{{ detailNode.bundleTotal }}</span>
                            </div>
                            <div class="cgraph-stat-cell">
                                <span class="cgraph-stat-lbl">On graph</span>
                                <span class="cgraph-stat-val">{{ detailNode.bundleDisplayed }}</span>
                            </div>
                            <div class="cgraph-stat-cell">
                                <span class="cgraph-stat-lbl">Collapsed</span>
                                <span class="cgraph-stat-val cgraph-stat-val--warn">{{ detailNode.bundleOverflow }}</span>
                            </div>
                        </div>
                        <div v-if="(detailNode.bundleHiddenAllIds || []).length" class="cgraph-btn-row">
                            <button type="button" class="btn btn-sm btn-outline-primary" @click="downloadTextFile('collapsed-ids.txt', (detailNode.bundleHiddenAllIds || []).join('\n'))">
                                <i class="fa fa-download me-1"></i>Download IDs
                            </button>
                            <button v-if="(detailNode.bundleHiddenAllIds || []).length > (detailNode.bundleHiddenPreview || []).length" type="button" class="btn btn-sm btn-outline-secondary" @click="bundlePanelShowAll = !bundlePanelShowAll">
                                {{ bundlePanelShowAll ? 'Show preview' : `Show all ${(detailNode.bundleHiddenAllIds || []).length}` }}
                            </button>
                        </div>
                        <template v-if="bundleCollapsedIdsDisplayed.length">
                            <div class="cgraph-section-lbl">
                                Collapsed IDs ({{ bundleCollapsedIdsDisplayed.length }}<template v-if="!bundlePanelShowAll && detailNode.bundleHiddenNotListed > 0"> of {{ (detailNode.bundleHiddenAllIds || []).length }}</template>)
                            </div>
                            <div class="cgraph-id-list">
                                <div v-for="(id, i) in bundleCollapsedIdsDisplayed" :key="'bi-'+i" class="cgraph-id-row">{{ id }}</div>
                            </div>
                        </template>
                    </template>

                    <!-- Entity (curation) -->
                    <template v-else-if="curationGraph && detailNode.type === 'entity'">
                        <div class="cgraph-section-lbl">Entity ID</div>
                        <div class="cgraph-mono-val">{{ detailNode.key }}</div>
                        <div class="cgraph-flag-row">
                            <span v-if="detailNode.isConnecting" class="cgraph-flag cgraph-flag--amber"><i class="fa fa-link me-1"></i>Connecting entity</span>
                            <span v-if="detailNode.inBothClusters" class="cgraph-flag cgraph-flag--amber"><i class="fa fa-code-branch me-1"></i>In both clusters</span>
                            <span v-if="detailNode.mergePreviewNew" class="cgraph-flag cgraph-flag--green"><i class="fa fa-plus me-1"></i>New from cluster 2</span>
                        </div>
                    </template>

                    <!-- Default -->
                    <template v-else>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">ID</span><span class="cgraph-mono-val small">{{ detailNode.id }}</span></div>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">Type</span><span class="cgraph-kv-val">{{ labels[detailNode.type] || detailNode.type }}</span></div>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">Domain</span><span class="cgraph-kv-val">{{ detailNode.domain }}</span></div>
                        <div class="cgraph-kv"><span class="cgraph-kv-lbl">Key</span><span class="cgraph-mono-val small">{{ detailNode.key }}</span></div>
                        <div v-if="detailNode.entityCount != null" class="cgraph-kv"><span class="cgraph-kv-lbl">Entities in list</span><span class="cgraph-kv-val fw-bold">{{ detailNode.entityCount }}</span></div>
                        <div class="cgraph-flag-row">
                            <span v-if="detailNode.inBothClusters" class="cgraph-flag cgraph-flag--amber">In both cluster lists</span>
                        </div>
                    </template>

                    <!-- Cluster edges (any node type) — the entity-level edges that join the
                         two clusters, read from `connecting_cluster_edges`. -->
                    <template v-if="(detailNode.clusterEdgeDetails || []).length">
                        <div class="cgraph-section-lbl">Cluster edges ({{ detailNode.clusterEdgeCount }})</div>
                        <div class="cgraph-id-list">
                            <div
                                v-for="(ce, ci) in detailNode.clusterEdgeDetails"
                                :key="'cle-' + ci"
                                class="cgraph-id-row cgraph-edge-row"
                            >
                                <span class="cgraph-edge-from">{{ ce.fromId }}</span>
                                <i class="fa fa-arrow-right cgraph-edge-arrow"></i>
                                <span v-if="ce.pairLabel" class="cgraph-edge-pair">{{ ce.pairLabel }}</span>
                                <span class="cgraph-edge-to">{{ ce.ids.join(', ') }}</span>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import * as d3 from "d3";

/** Below this many entries the legend always fits one row, so the wrap toggle is pointless. */
const LEGEND_WRAP_TOGGLE_MIN = 5;

/* Per-type fill colors — categorical palette, hue-spaced, no reds for data types */
const NODE_COLORS = {
    // Structural nodes — two distinct hub anchors; must NOT overlap data-type colors
    cluster:       "#6f42c1",  // purple-600   cluster hub 1  (left side in merge/split)
    cluster2:      "#0e7490",  // cyan-700     cluster hub 2  (right side in merge)

    // Semantic types — applied via neighborType prefix (e.g. "prsn~domain")
    prsn:          "#1d4ed8",  // blue-700     person / human identity
    cookie:        "#b45309",  // amber-700    browser cookie
    ck:            "#b45309",  // alias for cookie
    statid:        "#0f766e",  // teal-700     statistical identifier
    ip:            "#6d28d9",  // violet-700   IP address
    hh:            "#be185d",  // pink-700     household (warm, NOT error-red)
    org:           "#c2410c",  // orange-700   organization
    c_type:        "#0369a1",  // sky-700      configuration type
    hip:           "#7e22ce",  // purple-800   HIP biometric fingerprint
};

const NODE_LABELS = {
    prsn: "Person", cookie: "Cookie", ck: "Cookie",
    statid: "Stat ID", ip: "IP Address", hh: "Household",
    org: "Organization", c_type: "C Type", hip: "HIP",
    cluster: "Cluster", entity: "Entity",
    entity_group: "Neighbor Group", entity_bundle: "Collapsed",
};

/**
 * Golden-angle color generator — guarantees every index gets a unique,
 * maximally perceptually distinct hue. Works for any number of node types
 * (no palette wrapping). Lightness 57% keeps colors bright, not dark.
 *   hue(i) = (i × 137.508°) mod 360°
 */
function goldenColor(index) {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue.toFixed(1)}, 64%, 57%)`;
}

/* Font Awesome 6 Solid unicode points */
const NODE_ICONS = {
    prsn:          "",   /* user          */
    cookie:        "",   /* cookie        */
    ck:            "",
    statid:        "",   /* id-card       */
    ip:            "",   /* network-wired */
    hh:            "",   /* house         */
    org:           "",   /* building      */
    c_type:        "",   /* sliders       */
    hip:           "",   /* fingerprint   */
    cluster:       "",   /* users         */
    entity:        "",   /* fingerprint   */
    entity_group:  "",   /* layer-group   */
    entity_bundle: "",   /* database      */
};

const FA_CLASS_MAP = {
    prsn: "fa fa-user", cookie: "fa fa-cookie", ck: "fa fa-cookie",
    statid: "fa fa-id-card", ip: "fa fa-network-wired",
    hh: "fa fa-house", org: "fa fa-building", c_type: "fa fa-sliders",
    hip: "fa fa-fingerprint", cluster: "fa fa-users",
    entity: "fa fa-fingerprint", entity_group: "fa fa-layer-group",
    entity_bundle: "fa fa-database",
};

/**
 * Link kinds that annotate a relationship rather than cluster membership. They are drawn but
 * excluded from the force layout, from cluster-membership lookups, and from lane assignment.
 */
const ANNOTATION_LINK_KINDS = new Set(["connects", "bridge", "cluster_edge"]);

/**
 * The two cross-cluster line encodings. Both are dotted — neither is plain graph structure — and
 * they separate on dot size, weight and hue together, so they stay apart in grayscale and under
 * CVD rather than relying on hue alone:
 *
 *   connects      amber dots, 2.0px  — an annotation: this id is *eligible* for the cluster it points at
 *   cluster_edge  rose dots,  1.6–2.6px — an edge that exists, entity to entity across the clusters
 *   c1/c2         solid,      1.8px, cool — plain cluster membership (the skeleton), most recessive
 *
 * Amber outranks rose because of how the two scale. Real data yields a near-complete bipartite
 * cluster-edge set — 76 lines against 13 amber ones — so the layer that grows without bound has to
 * recede or it buries the layer a curator actually acts on. Rose therefore steps to a lighter,
 * thinner tint once it gets crowded (see clusterEdgeWeight), and amber always paints last.
 *
 * Rose replaced a fuchsia that measured only ΔE 6.1 (deutan) from the c1 blue — inside the weak
 * 6–8 band, which is why it read muddy. Every step below validates against its own surface, and
 * critically keeps its distance from the amber it must never be confused with: the light dense
 * tint holds ΔE 22.4 (normal) from amber, where the obvious lighter pick (#fda4af) collapsed to
 * 14.8 and would have made this worse, not better.
 */
const CLUSTER_EDGE_LINK_KIND = "cluster_edge";
const CLUSTER_EDGE_COLOR = { light: "#be185d", dark: "#f43f5e" };
/** Crowded graphs: a lighter tint so the amber connecting lines stay readable through them. */
const CLUSTER_EDGE_COLOR_DENSE = { light: "#f472b6", dark: "#f472b6" };
/* amber-700 in light mode: the mid amber washed out against the pink field at 2px. Holds
   ΔE 23.4 (normal) from the dense pink tint. Dark mode keeps its bright step — going darker
   there would sink it into the canvas. */
const CONNECTS_COLOR = { light: "#0e89ab", dark: "#fbbf24" };
/**
 * Round-capped near-zero dashes render as true dots rather than short dashes, and the cap makes
 * dot diameter track stroke width — so the heavier cluster-edge line also gets visibly bigger dots.
 */
const DOTTED_DASH = "0.1 5";
const CLUSTER_EDGE_DASH = "0.1 4.5";
const DOTTED_DASHES = new Set([DOTTED_DASH, CLUSTER_EDGE_DASH]);

let _uidCounter = 0;

export default {
    name: "D3CurationGraph",
    props: {
        data:            { type: Object,  default: null },
        nodeColors:      { type: Object,  default: () => ({}) },
        nodeLabels:      { type: Object,  default: () => ({}) },
        showBridgeStats: { type: Boolean, default: false },
        /** Merge curation: number of `connecting_entity_ids` for the selected recommendation. */
        connectingCount:  { type: Number,  default: 0 },
        /**
         * Merge curation: entity-to-entity edges named by `connecting_cluster_edges`. Counted at
         * the id level, the same unit the node inspector reports, so the two never disagree — a
         * single drawn line can carry several of them when its endpoint nodes hold several ids.
         */
        clusterEdgeCount: { type: Number,  default: 0 },
        curationGraph:   { type: Boolean, default: true },
        enableNodeMove:  { type: Boolean, default: false },
        /** ID of the entity_group node currently armed for partial migration (amber ring) */
        highlightNodeId: { type: String,  default: null },
    },
    emits: ["graph-updated", "node-moved", "group-node-clicked"],
    data() {
        return {
            hasGraph: false,
            isFullscreen: false,
            graphStats: { nodes: 0, edges: 0, sharedEntities: 0, collapsedEntities: 0 },
            legendItems: [],
            legendExpanded: false,
            hiddenTypes: new Set(),
            bundlePanelShowAll: false,
            graphInspector: { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] },
            graphInspectorHideTimer: null,
            detailNode: null,
            simulation: null,
            svg: null,
            zoomBehavior: null,
            graphNodes: [],
            graphEdges: [],
            graphUid: `cg${++_uidCounter}`,
            prefixColorMap: new Map(),   // neighborType-prefix → palette color, assigned per-graph
        };
    },
    computed: {
        /**
         * Every entry is always rendered. The legend is one horizontally scrollable row by default
         * — with these type~domain names it used to wrap to four rows and cost the canvas ~140px,
         * which is the wrong trade in a card whose whole job is the graph. The toggle switches to
         * a wrapped view for scanning them all at once.
         */
        visibleLegendItems() {
            const items = this.legendItems.filter(i => !i.legendOverflow);
            if (items.length < LEGEND_WRAP_TOGGLE_MIN) return items;
            return items.concat([{
                type: "__wrap__",
                legendOverflow: true,
                label: this.legendExpanded ? "One row" : `All ${items.length}`,
            }]);
        },
        colors() { return { ...NODE_COLORS, ...this.nodeColors }; },

        /**
         * Cluster edges are near-complete bipartite in real data — 14 entities on one side each
         * joined to the same 5 on the other is 76 lines over ~30 nodes. A weight that reads well
         * for a handful becomes a wall at that count, so past 20 the layer steps down to a lighter
         * tint and a thinner stroke: still legible as a group, no longer covering the amber.
         */
        clusterEdgeWeight() {
            const n = this.graphEdges.filter(e => e.linkKind === CLUSTER_EDGE_LINK_KIND).length;
            const dense = n > 20;
            const palette = dense ? CLUSTER_EDGE_COLOR_DENSE : CLUSTER_EDGE_COLOR;
            return {
                color: this.isDark ? palette.dark : palette.light,
                width: dense ? (n > 60 ? 1.5 : 1.8) : 2.6,
                opacity: dense ? (n > 60 ? 0.55 : 0.7) : 0.9,
            };
        },

        laneHeaders() {
            const hubs = this.graphNodes.filter(d => d.type === "cluster");
            if (hubs.length !== 2) return [];
            const byIndex = [1, 2].map(i => hubs.find(h => Number(h.clusterIndex) === i));
            if (byIndex.some(h => !h)) return [];
            return byIndex.map((h, i) => ({
                side: i === 0 ? "left" : "right",
                label: this.getTypeLabel(h),
                full: h.clusterFullId || h.key || "",
                color: this.nodeFill(h),
            }));
        },
        labels() { return { ...NODE_LABELS, ...this.nodeLabels }; },
        isDark() { return this.$store.state.darkMode; },
        bundleCollapsedIdsDisplayed() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_bundle") return [];
            const all = d.bundleHiddenAllIds;
            const prev = d.bundleHiddenPreview || [];
            if (Array.isArray(all) && all.length && this.bundlePanelShowAll) return all;
            return prev;
        },
        entityGroupDetailIds() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_group") return [];
            if (d.entityIds && d.entityIds.length) return d.entityIds.map(String).filter(Boolean);
            const subs = d.entitySubgroups || [];
            const flat = subs.flatMap(sg => (sg.entityIds || []).map(String)).filter(Boolean);
            return [...new Set(flat)];
        },
    },
    watch: {
        detailNode() { this.bundlePanelShowAll = false; },
        data: {
            handler(val) {
                if (val && val.nodes && val.edges) this.$nextTick(() => this.loadGraph(val));
            },
            deep: true,
        },
        isDark() {
            if (this.graphNodes.length > 0) this.$nextTick(() => this.drawGraph());
        },
        highlightNodeId(newId, oldId) {
            if (!this.svg) return;
            const g = this.svg.select("g");
            // Remove ring from previously highlighted node
            if (oldId) {
                g.selectAll(".cgn").filter(d => d.id === oldId)
                    .select(".cgn-circle")
                    .transition().duration(200)
                    .attr("stroke",       d => this.nodeCircleStroke(d).color)
                    .attr("stroke-width", d => this.nodeCircleStroke(d).width);
            }
            // Apply amber pulsing ring to new highlighted node
            if (newId) {
                g.selectAll(".cgn").filter(d => d.id === newId)
                    .select(".cgn-circle")
                    .attr("stroke", "#fbbf24")
                    .attr("stroke-width", 7);
            }
        },
    },
    mounted() {
        document.querySelectorAll(".cgraph-float-tip").forEach(el => el.remove());
        if (this.data && this.data.nodes && this.data.edges) {
            this.$nextTick(() => this.loadGraph(this.data));
        }
        if (typeof ResizeObserver !== "undefined" && this.$refs.graphContainer) {
            let _t = null;
            this._ro = new ResizeObserver(() => {
                clearTimeout(_t);
                _t = setTimeout(() => { if (this.svg && this.hasGraph) this.fitToScreen(); }, 120);
            });
            this._ro.observe(this.$refs.graphContainer);
        }
    },
    beforeUnmount() {
        if (this.simulation) this.simulation.stop();
        this._clearInspTimer();
        if (this._ro) { this._ro.disconnect(); this._ro = null; }
        document.querySelectorAll(".cgraph-float-tip").forEach(el => el.remove());
    },
    methods: {
        /* ── Color / icon helpers ─────────────────────────────────────── */
        getColor(type) { return this.colors[type] || "#64748b"; },

        getNodeIcon(d) {
            /* Extract the entity type prefix from neighborType (e.g. "prsn~aqfer.com" → "prsn") */
            if (d.neighborType) {
                const prefix = String(d.neighborType).split("~")[0].trim().toLowerCase();
                if (NODE_ICONS[prefix]) return NODE_ICONS[prefix];
            }
            return NODE_ICONS[d.type] || '';
        },

        /**
         * Shorten a `type~domain` key for a node label or legend chip.
         *
         * Real domains share long prefixes — `ck~c900_z__z_neutralfeed.com_z_aqferPage_default…`
         * and `ck~c900_z__z_neutralfeed.com_z_aqferBcnLcy_z_` differ only at the tail. Cutting the
         * end (the obvious approach) renders every one of them identically, so the type is kept
         * whole, a short head of the domain is kept for orientation, and the rest of the budget
         * goes to the tail that actually tells them apart.
         */
        shortPairLabel(raw, max) {
            const s = String(raw || "").trim();
            if (!s || s.length <= max) return s;
            const tilde = s.indexOf("~");
            if (tilde < 0) return `${s.slice(0, Math.max(1, max - 1))}…`;
            const type = s.slice(0, tilde);
            const domain = s.slice(tilde + 1);
            /* Budget left for the domain once "type~" and the ellipsis are paid for. */
            const room = max - type.length - 2;
            if (room < 6) return `${type}~…${domain.slice(-Math.max(3, room))}`;
            const head = Math.min(6, Math.floor(room / 3));
            return `${type}~${domain.slice(0, head)}…${domain.slice(-(room - head))}`;
        },

        getTypeLabel(d) {
            if (d.type === "cluster") {
                if (this.curationGraph) {
                    if (d.isSplitNew) {
                        const hash = d.clusterFullId || d.key || "";
                        return hash ? `Cluster ${hash.slice(0, 8)}` : `New Cluster ${Number(d.clusterIndex) - 1}`;
                    }
                    return d.clusterIndex != null ? `Cluster ${d.clusterIndex}` : "Cluster";
                }
                return this.labels.cluster || "Cluster";
            }
            /* A cluster-edge endpoint is a lone entity split out of its type~domain group, so it
               keeps the group's label — "Entity" would lose the only context it has. */
            if (this.curationGraph && d.neighborType && (d.type === "entity_group" || d.type === "entity")) {
                return this.shortPairLabel(d.neighborType || d.key || "", 26) || "Group";
            }
            return this.labels[d.type] || d.type || "";
        },

        getKeyLabel(d, isLarge) {
            const max = isLarge ? 8 : 12;
            if (d.type === "cluster") {
                const cnt = Number(d.hubConnectionCount) || 0;
                return `${cnt} nodes`;
            }
            if (d.type === "entity_group") {
                const n = this._entityGroupIdCount(d);
                /* A split-off connecting node holds only connecting ids — say so, so the amber ring
                   is never read as "every id in this group connects". */
                if (d.isConnecting && d.connectingIdCount >= n) return `${n} connecting`;
                return `${n} ids`;
            }
            if (d.type === "entity_bundle") {
                const n = Number(d.bundleTotal);
                if (d.connectingIdCount) return `${d.connectingIdCount} of ${n} connecting`;
                return Number.isFinite(n) ? `${n} total` : "";
            }
            const k = String(d.key || "").trim();
            if (!k) return "";
            return k.length <= max ? k : `…${k.slice(-(max - 1))}`;
        },

        panelTitle(d) {
            if (!d) return "";
            if (this.curationGraph && d.type === "entity_group" && d.neighborType) return d.neighborType;
            if (this.curationGraph && d.type === "entity_bundle") return this.labels.entity_bundle || "Collapsed entities";
            return this.labels[d.type] || d.type;
        },

        panelIconClass(d) {
            return FA_CLASS_MAP[d.type] || "fa fa-circle";
        },

        nodeCircleStroke(d) {
            if (d.pinned)           return { color: "#f59e0b", width: 3.5 };
            if (d.mergePreviewNew)  return { color: "#10b981", width: 3.5 };
            if (d.inBothClusters)   return { color: "#f59e0b", width: 3.5 };
            if (this.curationGraph && d.isMoved) {
                return { color: "#10b981", width: 4 };  // green: IDs that were migrated
            }
            if (this.curationGraph && d.type === "cluster" && d.isSplitNew) {
                return { color: "#f59e0b", width: 4 };
            }
            if (this.curationGraph && d.type === "entity_group") {
                if (d.nodeLinkKind === "c1") return { color: "#3b82f6", width: 3 };
                if (d.nodeLinkKind === "c2") return { color: "#10b981", width: 3 };
                if (d.nodeLinkKind === "c3") return { color: "#f43f5e", width: 3 };
                if (d.nodeLinkKind === "c4") return { color: "#f59e0b", width: 3 };
            }
            return { color: this.isDark ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.85)", width: 2.5 };
        },

        neighborTypeFill(d) {
            if (!d || d.neighborType == null) return null;
            const nt = String(d.neighborType).trim();
            if (!nt) return null;
            const ty = d.type;
            if (ty != null && ty !== "entity" && ty !== "entity_group") return null;
            /* Use full "type~domain" as key so each unique combination gets its own color */
            return this.prefixColorMap.get(nt.toLowerCase()) || null;
        },

        nodeFill(d) {
            /* Cluster hubs — distinct colors that never clash with data-type colors */
            if (d.type === "cluster") {
                const idx = Number(d.clusterIndex ?? 2);
                if (idx === 1) return this.colors["cluster"]  || "#6f42c1";
                if (idx === 2) return this.colors["cluster2"] || "#0e7490";
                // Additional split clusters get distinct warm/cool alternating colors
                const extras = ["#e11d48", "#d97706", "#059669", "#7c3aed", "#db2777", "#0891b2"];
                return extras[(idx - 3) % extras.length] || "#64748b";
            }
            if (d.neighborType) {
                /* Full "type~domain" key first → every unique combination gets its own color */
                const ntKey = String(d.neighborType).trim().toLowerCase();
                const byFull = this.prefixColorMap.get(ntKey);
                if (byFull) return byFull;
                /* Fallback: prefix semantic (node seen outside of current graph load) */
                const prefix = ntKey.split("~")[0];
                if (this.colors[prefix]) return this.colors[prefix];
            }
            /* No neighborType (entity, entity_bundle, unknown types) */
            const byType = this.prefixColorMap.get(d.type);
            if (byType) return byType;
            if (this.colors[d.type]) return this.colors[d.type];
            return "#64748b";
        },

        nodeRadius(d) {
            const base = 22;
            if (d.type === "cluster") {
                if (this.curationGraph) {
                    const c = Number(d.hubConnectionCount) || 0;
                    return base + Math.min(22, 8 + Math.floor(c / 5));
                }
                return base + Math.min(12, Math.floor((Number(d.entityCount) || 0) / 2));
            }
            if (d.type === "entity_group") {
                const nd = (d.entitySubgroups && d.entitySubgroups.length) || 1;
                let r = base + Math.min(12, 2 + nd * 2);
                if (this.curationGraph) {
                    const raw = String(d.neighborType || d.key || "");
                    const tl = (raw.indexOf("~") >= 0 ? raw.slice(0, raw.indexOf("~")) : raw).length;
                    r += Math.min(6, Math.floor(tl / 4) + 1);
                }
                return r;
            }
            if (d.type === "entity_bundle") {
                const ov = Number(d.bundleOverflow) || 0;
                return base + Math.min(14, 4 + Math.log2(ov + 2) * 2.5);
            }
            return d.inBothClusters ? base + 4 : base;
        },

        linkStroke(e) {
            const k = e.linkKind;
            if (k === "pair")   return this.isDark ? "#a78bfa" : "#8b5cf6";
            if (k === "c1")     return this.isDark ? "#93c5fd" : "#3b82f6";
            if (k === "c2")     return this.isDark ? "#6ee7b7" : "#10b981";
            if (k === "c3")     return this.isDark ? "#fda4af" : "#f43f5e";
            if (k === "c4")     return this.isDark ? "#fcd34d" : "#f59e0b";
            if (k === "c5")     return this.isDark ? "#6ee7b7" : "#059669";
            if (k === "c6")     return this.isDark ? "#c4b5fd" : "#7c3aed";
            /* rose — real cluster-joining edges from `connecting_cluster_edges` */
            if (k === CLUSTER_EDGE_LINK_KIND) return this.clusterEdgeWeight.color;
            /* amber — cross-cluster connectors */
            if (ANNOTATION_LINK_KINDS.has(k)) return this.isDark ? CONNECTS_COLOR.dark : CONNECTS_COLOR.light;
            return this.isDark ? "#2d3748" : "#e2e8f0";
        },

        linkDash(e) {
            if (e.linkKind === "pair") return "6 4";
            if (e.linkKind === CLUSTER_EDGE_LINK_KIND) return CLUSTER_EDGE_DASH;
            if (ANNOTATION_LINK_KINDS.has(e.linkKind)) return DOTTED_DASH;
            return "";
        },

        /** Round caps turn the near-zero dashes of a cross-cluster line into dots. */
        linkCap(e) {
            return DOTTED_DASHES.has(this.linkDash(e)) ? "round" : "butt";
        },

        linkWidth(e) {
            if (e.linkKind === "pair") return 2.5;
            if (e.linkKind === CLUSTER_EDGE_LINK_KIND) return this.clusterEdgeWeight.width;
            if (ANNOTATION_LINK_KINDS.has(e.linkKind)) return 2.0;
            return 1.8;
        },

        linkOpacity(e) {
            if (e.linkKind === "pair") return 0.85;
            if (e.linkKind === CLUSTER_EDGE_LINK_KIND) return this.clusterEdgeWeight.opacity;
            if (ANNOTATION_LINK_KINDS.has(e.linkKind)) return 0.95;
            return 0.55;
        },

        /* ── Graph lifecycle ──────────────────────────────────────────── */
        loadGraph(inputData) {
            this._clearInspTimer();
            this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };

            // Save current positions + linkKind so we can detect membership changes on re-render
            if (!this._posCache) this._posCache = new Map();
            for (const n of this.graphNodes) {
                if (n.x != null && n.y != null) {
                    this._posCache.set(n.id, { x: n.x, y: n.y, nodeLinkKind: n.nodeLinkKind ?? null });
                }
            }

            // Detect which nodes changed cluster membership (override added OR removed).
            // We still restore their positions so there's no jump — the simulation will
            // pull them smoothly to the correct lane with a higher alpha target.
            let membershipChangedCount = 0;
            this.graphNodes = inputData.nodes.map(n => {
                const prev = this._posCache.get(n.id);
                if (!prev) return { ...n };
                // Always restore position (visual continuity, no jump to origin)
                if (n.type !== 'cluster') {
                    const prevKind = prev.nodeLinkKind;
                    const newKind = n.nodeLinkKind ?? null;
                    if (prevKind !== newKind) membershipChangedCount++;
                }
                return { ...n, x: prev.x, y: prev.y };
            });
            this._membershipChangedCount = membershipChangedCount;

            this.graphEdges = inputData.edges.map(e => {
                const src = typeof e.source === "object" ? e.source.id : e.source;
                const tgt = typeof e.target === "object" ? e.target.id : e.target;
                const out = { source: src, target: tgt };
                if (e.linkKind) out.linkKind = e.linkKind;
                return out;
            });

            /* Build a sequential color map keyed by the FULL neighborType value
               (e.g. "prsn~aqfer.com") so every unique type~domain combination
               gets its own distinct color, not just every unique prefix.
               Pass 1: full neighborType values.
               Pass 2: bare node types for nodes without a neighborType. */
            const colorMap = new Map();
            let palIdx = 0;
            const assignKey = key => {
                if (!key || colorMap.has(key)) return;
                colorMap.set(key, goldenColor(palIdx));
                palIdx++;
            };
            this.graphNodes.forEach(n => {
                if (n.type === "cluster") return;
                if (n.neighborType) {
                    assignKey(String(n.neighborType).trim().toLowerCase());
                }
            });
            this.graphNodes.forEach(n => {
                if (n.type === "cluster") return;
                if (!n.neighborType) assignKey(n.type);
            });
            this.prefixColorMap = colorMap;

            this.hasGraph = true;
            this.updateStats();
            this.$nextTick(() => this.drawGraph());
        },

        drawGraph() {
            const container = this.$refs.graphContainer;
            if (!container) return;

            const W = container.clientWidth;
            const H = container.clientHeight || 600;
            const isLarge = this.graphNodes.length > 200 || this.graphEdges.length > 250;
            const isDark = this.isDark;
            const vm = this;
            const nr = d => this.nodeRadius(d);

            d3.select(container).select("svg").remove();

            const svg = d3.select(container)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${W} ${H}`);

            /* ── SVG defs: shadow filter + arrowhead ── */
            const defs = svg.append("defs");
            const sid = `${this.graphUid}-shadow`;
            const f = defs.append("filter")
                .attr("id", sid)
                .attr("x", "-50%").attr("y", "-50%")
                .attr("width", "200%").attr("height", "200%");
            f.append("feDropShadow")
                .attr("dx", 0).attr("dy", 2.5)
                .attr("stdDeviation", isDark ? 5 : 4)
                .attr("flood-color", isDark ? "rgba(0,0,0,0.6)" : "rgba(15,23,42,0.22)")
                .attr("flood-opacity", 1);

            const g = svg.append("g");

            this.zoomBehavior = d3.zoom()
                .scaleExtent([0.04, 2.2])
                .on("zoom", event => g.attr("transform", event.transform));
            svg.call(this.zoomBehavior);

            svg.on("click", () => {
                this._clearInspTimer();
                this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
                this._clearHighlight(g);
                this.detailNode = null;
            });

            this.svg = svg;

            /* ── Simulation ── */
            const nodeCount = this.graphNodes.length;
            const isXLarge  = nodeCount > 350;
            let   isDragging = false;

            /* Which cluster (1 or 2) does a node belong to? 0 = neutral */
            const clusterOf = d => {
                if (d.type === "cluster") return Number(d.clusterIndex ?? 2) === 1 ? 1 : 2;
                /* entity_bundle nodes carry bundleLinkKind instead of nodeLinkKind — without it
                   they read as neutral and get pulled to the centre, between the two hubs. */
                const lk = d.nodeLinkKind || d.bundleLinkKind;
                if (lk === "c1") return 1;
                if (lk === "c2") return 2;
                return 0;
            };

            /* Per-cluster node counts — used to size lanes and collision adaptively */
            const c1Count  = this.graphNodes.filter(d => clusterOf(d) === 1).length || 1;
            const c2Count  = this.graphNodes.filter(d => clusterOf(d) === 2).length || 1;
            const bigSide  = Math.max(c1Count, c2Count);

            /* Adaptive parameters that scale with the largest cluster */
            const charge   = Math.min(-800, -800 - bigSide * 6);          // stronger repulsion
            const dist     = Math.max(180, 150 + Math.sqrt(bigSide) * 9); // longer links
            const collPad  = Math.max(60, Math.sqrt(bigSide) * 10);
            /* Stronger separation so clusters start clearly apart (min 0.45) */
            const sepStr   = Math.min(0.75, 0.45 + bigSide * 0.004);
            /* Wider lanes: clusters start further left/right */
            const halfSpan = Math.min(0.42, 0.22 + Math.sqrt(bigSide) * 0.025);

            /* A hub's children settle on a ring around it, and that ring has to be wide enough
               to seat every child's collision circle: count · 2(childR + collPad) ≤ 2πR.
               Two hubs closer together than the sum of their rings make the clusters visually
               overlap in the middle, so lane width and hub separation are derived from the ring
               sizes — not from canvas width alone. The layout may end up wider than the canvas;
               fitToScreen() frames it afterwards. */
            const CHILD_RADIUS_EST = 34;
            const ringRadius = (childCount) => {
                const c = Math.max(0, Number(childCount) || 0);
                return c < 2 ? 0 : (c * (CHILD_RADIUS_EST + collPad)) / Math.PI;
            };
            const ringByHubId = new Map(
                this.graphNodes.filter(d => d.type === "cluster").map(h => [h.id, ringRadius(h.hubConnectionCount)])
            );
            const widestRing = ringByHubId.size ? Math.max(...ringByHubId.values()) : 0;
            const laneHalf   = Math.max(W * halfSpan, widestRing + collPad);
            const cx         = W / 2;
            /* World x for a cluster lane: 1 → left, 2+ → right, 0 (neutral) → centre. */
            const laneX = c => (c === 1 ? cx - laneHalf : c === 2 ? cx + laneHalf : cx);
            const laneGutter = Math.max(70, laneHalf * 0.18);

            const seedSeen = { 1: 0, 2: 0 };
            this.graphNodes.forEach(n => {
                if (n.x != null) return;
                const c = clusterOf(n);
                if (n.type === "cluster") {
                    n.x = laneX(Number(n.clusterIndex ?? 2) === 1 ? 1 : 2);
                    n.y = H * 0.5;
                    return;
                }
                if (!c) { n.x = cx; n.y = H * 0.5; return; }
                const i = seedSeen[c]++;
                const total = Math.max(1, (c === 1 ? c1Count : c2Count) - 1);
                const r = Math.max(150, ringRadius(total));
                const angle = (c === 1 ? Math.PI / 2 : -Math.PI / 2) + (Math.PI * (i + 0.5)) / total;
                n.x = laneX(c) + Math.cos(angle) * r;
                n.y = H * 0.5 + Math.sin(angle) * r;
            });

            /* Annotation edges ('connects', 'bridge') are visual-only: resolve their node refs
               manually so the tick function can read .x/.y, but exclude them from forceLink so
               they don't exert attraction between the two clusters. */
            const nodeById = new Map(this.graphNodes.map(n => [n.id, n]));
            const simEdges = [];
            for (const e of this.graphEdges) {
                if (ANNOTATION_LINK_KINDS.has(e.linkKind)) {
                    e.source = nodeById.get(typeof e.source === 'object' ? e.source.id : e.source) || e.source;
                    e.target = nodeById.get(typeof e.target === 'object' ? e.target.id : e.target) || e.target;
                } else {
                    simEdges.push(e);
                }
            }

            this.simulation = d3.forceSimulation(this.graphNodes)
                .force("link",      d3.forceLink(simEdges).id(d => d.id).distance(dist))
                .force("charge",    d3.forceManyBody().strength(charge).theta(isXLarge ? 0.92 : 0.80))
                .force("collision", d3.forceCollide().radius(d => nr(d) + collPad).iterations(4))
                /* Pull each cluster node to its own horizontal lane — suppressed during drag.
                   When nodes just changed membership (override added/removed) we boost strength
                   so they reach the correct lane before the simulation cools. */
                .force("clusterX",  d3.forceX(d => laneX(clusterOf(d))).strength(d => {
                    if (isDragging) return 0.02;
                    if (clusterOf(d) === 0) return 0.05;
                    if (d.type === "cluster") return 0.95;
                    const boost = (vm._membershipChangedCount || 0) > 0 ? 0.25 : 0;
                    return Math.min(0.9, sepStr + boost);
                }))
                .force("clusterY",  d3.forceY(H / 2).strength(d => d.type === "cluster" ? 0.35 : 0.05))
                /* Hard repulsion between hub nodes — ensures they stay apart even on small graphs */
                .force("hubRepel",  alpha => {
                    if (isDragging) return;
                    const hubs = vm.graphNodes.filter(d => d.type === "cluster");
                    if (hubs.length < 2) return;
                    for (let i = 0; i < hubs.length; i++) {
                        for (let j = i + 1; j < hubs.length; j++) {
                            const a = hubs[i], b = hubs[j];
                            if (a.x == null || b.x == null) continue;
                            const dx = b.x - a.x;
                            const d2 = Math.abs(dx) || 1;
                            /* Minimum separation: enough for both hubs' child rings to clear each
                               other, never less than 55% of the canvas width. */
                            const minD = Math.max(
                                W * 0.55,
                                Math.sqrt(bigSide) * 50,
                                (ringByHubId.get(a.id) || 0) + (ringByHubId.get(b.id) || 0) + collPad
                            );
                            if (d2 < minD) {
                                const f = alpha * 1.0 * (minD - d2) / d2;
                                a.vx -= dx * f;
                                b.vx += dx * f;
                            }
                        }
                    }
                })
                .force("lane", () => {
                    if (isDragging) return;
                    for (const n of vm.graphNodes) {
                        const c = clusterOf(n);
                        if (!c || n.x == null) continue;
                        if (n.type === "cluster") { n.y = H * 0.5; n.vy = 0; }
                        if (c === 1) {
                            const limit = cx - laneGutter;
                            if (n.x > limit) { n.x = limit; if (n.vx > 0) n.vx = 0; }
                        } else {
                            const limit = cx + laneGutter;
                            if (n.x < limit) { n.x = limit; if (n.vx < 0) n.vx = 0; }
                        }
                    }
                })
                .velocityDecay(isXLarge ? 0.62 : 0.48)
                .alphaDecay(isXLarge ? 0.055 : 0.022);

            // Tune simulation energy based on what changed:
            const totalNodes = this.graphNodes.length;
            const positionedCount = this.graphNodes.filter(n => n.x != null).length;
            const allPositioned = totalNodes > 0 && positionedCount === totalNodes;
            const changed = this._membershipChangedCount || 0;

            if (allPositioned && changed > 0) {
                // Nodes changed cluster (override added/removed): pull them to correct lane
                this.simulation.alpha(0.65).alphaDecay(0.035);
            } else if (allPositioned) {
                // Pure cosmetic re-render: settle instantly
                this.simulation.alpha(0.12).alphaDecay(0.08);
            } else if (positionedCount > 0 && positionedCount / totalNodes >= 0.7) {
                // Mostly positioned — a few new nodes (e.g. moved-IDs split node appeared):
                // moderate energy so new nodes settle near their target cluster quickly
                this.simulation.alpha(0.5).alphaDecay(0.04);
            }
            // else: first load or many new nodes → default alpha(1)

            this._membershipChangedCount = 0;  // reset so it doesn't affect drag-restart calls

            /* ── Edges ── */
            /* SVG paints in document order, so the layer a curator acts on is sorted last:
               membership, then cluster edges, then the amber connecting lines on top. Sorting here
               rather than relying on the order the graph builder happened to push them in. */
            const paintRank = e =>
                e.linkKind === CLUSTER_EDGE_LINK_KIND ? 1 : ANNOTATION_LINK_KINDS.has(e.linkKind) ? 2 : 0;
            const link = g.selectAll(".cge")
                .data(this.graphEdges.slice().sort((a, b) => paintRank(a) - paintRank(b))).enter()
                .append("line")
                .attr("class", "cge")
                .attr("stroke",           d => this.linkStroke(d))
                .attr("stroke-width",     d => this.linkWidth(d))
                .attr("stroke-dasharray", d => this.linkDash(d) || null)
                .attr("stroke-linecap",   d => this.linkCap(d))
                .attr("stroke-opacity",   d => this.linkOpacity(d));

            /* ── Helper: find current cluster hub id for a node via edges ── */
            const getId = v => (typeof v === "object" && v !== null) ? v.id : v;
            const findNodeCluster = (nodeId) => {
                for (const e of vm.graphEdges) {
                    if (ANNOTATION_LINK_KINDS.has(e.linkKind)) continue;
                    const src = getId(e.source);
                    const tgt = getId(e.target);
                    if (src === nodeId) {
                        const tn = vm.graphNodes.find(n => n.id === tgt);
                        if (tn && tn.type === "cluster") return tgt;
                    } else if (tgt === nodeId) {
                        const sn = vm.graphNodes.find(n => n.id === src);
                        if (sn && sn.type === "cluster") return src;
                    }
                }
                return null;
            };

            /* ── Node groups ── */
            let dragTarget = null;  // cluster node currently in drop zone during drag

            const node = g.selectAll(".cgn")
                .data(this.graphNodes).enter()
                .append("g")
                .attr("class", "cgn")
                .call(
                    d3.drag()
                        /* ── Drag start ── */
                        .on("start", (ev, d) => {
                            // Always stop SVG-level zoom/pan from hijacking the drag
                            if (ev.sourceEvent) ev.sourceEvent.stopPropagation();

                            if (vm.enableNodeMove && d.type !== "cluster") {
                                // Node-move mode: pin node in place, do NOT reheat simulation
                                d.fx = d.x; d.fy = d.y;
                                return;
                            }

                            // Normal layout drag: reheat simulation
                            isDragging = true;
                            if (!ev.active) vm.simulation.alphaTarget(0.1).restart();
                            d.fx = d.x; d.fy = d.y;
                        })

                        /* ── Drag move ── */
                        .on("drag", (ev, d) => {
                            d.fx = ev.x; d.fy = ev.y;

                            if (!vm.enableNodeMove || d.type === "cluster") return;

                            // Detect which cluster hub the node is hovering over
                            const hubs = vm.graphNodes.filter(n => n.type === "cluster");
                            let nearest = null;
                            let nearestDist = Infinity;
                            for (const hub of hubs) {
                                if (hub.x == null || hub.y == null) continue;
                                const dx = ev.x - hub.x;
                                const dy = ev.y - hub.y;
                                const dist = Math.sqrt(dx * dx + dy * dy);
                                // Drop zone: node radius + generous padding so it's easy to hit
                                if (dist < nr(hub) + 90 && dist < nearestDist) {
                                    nearestDist = dist;
                                    nearest = hub;
                                }
                            }

                            if (nearest !== dragTarget) {
                                // Reset previous target highlight
                                if (dragTarget) {
                                    g.selectAll(".cgn").filter(n => n === dragTarget)
                                        .select(".cgn-circle")
                                        .attr("stroke", () => vm.nodeCircleStroke(dragTarget).color)
                                        .attr("stroke-width", () => vm.nodeCircleStroke(dragTarget).width);
                                }
                                if (nearest) {
                                    // Highlight new drop target with green ring
                                    g.selectAll(".cgn").filter(n => n === nearest)
                                        .select(".cgn-circle")
                                        .attr("stroke", "#10b981")
                                        .attr("stroke-width", 7);
                                }
                                dragTarget = nearest;
                            }
                        })

                        /* ── Drag end ── */
                        .on("end", (ev, d) => {
                            // Always reset cluster highlight
                            if (dragTarget) {
                                g.selectAll(".cgn").filter(n => n === dragTarget)
                                    .select(".cgn-circle")
                                    .attr("stroke", () => vm.nodeCircleStroke(dragTarget).color)
                                    .attr("stroke-width", () => vm.nodeCircleStroke(dragTarget).width);
                            }

                            if (vm.enableNodeMove && d.type !== "cluster") {
                                // Emit move if dropped on a different cluster
                                if (dragTarget) {
                                    const fromClusterId = findNodeCluster(d.id);
                                    const toClusterId = dragTarget.id;
                                    if (fromClusterId && fromClusterId !== toClusterId) {
                                        vm.$emit("node-moved", {
                                            nodeId: d.id,
                                            node: { ...d },
                                            fromClusterId,
                                            toClusterId,
                                        });
                                    }
                                }
                                dragTarget = null;
                                if (!d.pinned) { d.fx = null; d.fy = null; }
                                // Brief low-alpha restart to let layout settle after edge change
                                if (!ev.active) {
                                    vm.simulation.alphaTarget(0.04).restart();
                                    setTimeout(() => vm.simulation.alphaTarget(0), 500);
                                }
                                return;
                            }

                            // Normal layout drag end
                            dragTarget = null;
                            isDragging = false;
                            if (!ev.active) vm.simulation.alphaTarget(0);
                            if (!d.pinned) { d.fx = null; d.fy = null; }
                        })
                );

            /* Main filled circle */
            node.append("circle")
                .attr("class", "cgn-circle")
                .attr("r",            d => nr(d))
                .attr("fill",         d => this.nodeFill(d))
                .attr("stroke",       d => this.nodeCircleStroke(d).color)
                .attr("stroke-width", d => this.nodeCircleStroke(d).width)
                .style("filter",      `url(#${sid})`);

            /* Connecting-entity halo (merge curation): node carries at least one
               `connecting_entity_ids` member, i.e. it links the two clusters. */
            node.filter(d => !!d.isConnecting)
                .append("circle")
                .attr("class", "cgn-connecting-ring")
                .attr("r",               d => nr(d) + 5)
                .attr("fill",            "none")
                .attr("stroke",          isDark ? CONNECTS_COLOR.dark : CONNECTS_COLOR.light)
                .attr("stroke-width",    2)
                .attr("stroke-dasharray", DOTTED_DASH)
                .attr("stroke-linecap",  "round")
                .attr("pointer-events",  "none");

            /* Cluster-edge halo: this node holds an endpoint of a `connecting_cluster_edges`
               edge. Coarse rose dots outside the fine amber ones, so each ring repeats the dot
               size and hue of the line it belongs to and a node can carry both. */
            node.filter(d => !!d.hasClusterEdge)
                .append("circle")
                .attr("class", "cgn-cluster-edge-ring")
                .attr("r",               d => nr(d) + (d.isConnecting ? 10 : 5))
                .attr("fill",            "none")
                .attr("stroke",          this.clusterEdgeWeight.color)
                .attr("stroke-width",    this.clusterEdgeWeight.width)
                .attr("stroke-dasharray", CLUSTER_EDGE_DASH)
                .attr("stroke-linecap",  "round")
                .attr("pointer-events",  "none");

            /* Inner highlight ring */
            node.append("circle")
                .attr("r",    d => nr(d) * 0.68)
                .attr("fill", "rgba(255,255,255,0.10)")
                .attr("pointer-events", "none");

            /* Font Awesome icon */
            node.append("text")
                .attr("class", "cgn-icon")
                .attr("text-anchor",      "middle")
                .attr("dominant-baseline","central")
                .attr("dy",               "0.05em")
                .attr("font-family",      "'Font Awesome 6 Free','Font Awesome 5 Free','FontAwesome'")
                .attr("font-weight",      "900")
                .attr("font-size",        d => `${Math.round(nr(d) * (isLarge ? 0.48 : 0.52))}px`)
                .attr("fill",             "rgba(255,255,255,0.93)")
                .attr("pointer-events",   "none")
                .attr("style",            "text-rendering:geometricPrecision")
                .text(d => vm.getNodeIcon(d));

            /* Type label (below node) */
            node.append("text")
                .attr("class",          "cgn-type")
                .attr("text-anchor",    "middle")
                .attr("y",              d => nr(d) + (isLarge ? 12 : 15))
                .attr("font-size",      isLarge ? "9.5px" : "11px")
                .attr("font-weight",    "600")
                .attr("letter-spacing", "0.01em")
                .attr("fill",           isDark ? "#cbd5e1" : "#1e293b")
                .attr("pointer-events", "none")
                .text(d => vm.getTypeLabel(d));

            /* Key / count label (below type) */
            node.append("text")
                .attr("class",          "cgn-key")
                .attr("text-anchor",    "middle")
                .attr("y",              d => nr(d) + (isLarge ? 23 : 28))
                .attr("font-size",      isLarge ? "8px" : "9.5px")
                .attr("fill",           isDark ? "#64748b" : "#94a3b8")
                .attr("pointer-events", "none")
                .attr("font-family",    "ui-monospace,monospace")
                .text(d => vm.getKeyLabel(d, isLarge));

            /* ── Move cursor for draggable entity nodes ── */
            if (vm.enableNodeMove) {
                node.style("cursor", d => d.type !== "cluster" ? "move" : null);
            }

            /* ── Interaction ── */
            node.on("click", (ev, d) => {
                ev.stopPropagation();
                vm._clearInspTimer();
                vm.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
                vm._highlightNeighbors(d, g);

                // In node-move mode, clicking an entity_group opens the ID selector panel.
                // Single-entity and cluster nodes open the detail panel as normal.
                if (vm.enableNodeMove && d.type === "entity_group") {
                    const ids = d.entityIds && d.entityIds.length
                        ? d.entityIds
                        : (d.entitySubgroups || []).flatMap(sg => sg.entityIds || []);
                    if (ids.length > 1) {
                        vm.$emit("group-node-clicked", { nodeId: d.id, node: { ...d } });
                        vm.detailNode = null;  // close detail panel; parent shows selection panel
                        return;
                    }
                }

                vm.detailNode = d;
            });

            node.on("dblclick", (ev, d) => {
                ev.stopPropagation();
                d.pinned = !d.pinned;
                d.fx = d.pinned ? d.x : null;
                d.fy = d.pinned ? d.y : null;
                d3.select(ev.currentTarget).select("circle")
                    .attr("stroke",       () => this.nodeCircleStroke(d).color)
                    .attr("stroke-width", () => this.nodeCircleStroke(d).width);
            });

            node.on("mouseover", (ev, d) => {
                this._clearInspTimer();
                if (this.curationGraph) {
                    this._setInspectorFromNode(d);
                } else {
                    this._showFloatTip(ev, d, container);
                }
            });

            node.on("mouseout", () => {
                if (this.curationGraph) {
                    this._scheduleInspHide();
                } else {
                    const tip = container.querySelector(".cgraph-float-tip");
                    if (tip) tip.style.opacity = "0";
                }
            });

            /* ── Tick ── */
            this.simulation.on("tick", () => {
                link.attr("x1", d => d.source.x).attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x).attr("y2", d => d.target.y);
                node.attr("transform", d => `translate(${d.x},${d.y})`);
            });

            this.simulation.on("end", () => this.fitToScreen());
        },

        /* ── Inspector bar (curation hover) ──────────────────────────── */
        _clearInspTimer() {
            if (this.graphInspectorHideTimer != null) {
                clearTimeout(this.graphInspectorHideTimer);
                this.graphInspectorHideTimer = null;
            }
        },
        _scheduleInspHide() {
            this._clearInspTimer();
            this.graphInspectorHideTimer = setTimeout(() => {
                this.graphInspectorHideTimer = null;
                this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
            }, 450);
        },
        _setInspectorFromNode(d) {
            const conns = this._connCount(d);
            const color = this.nodeFill(d);
            const lines = [];
            let fullLabel = "";

            if (d.type === "entity_group") {
                /* Show full type~domain as the chip label; prefix if these are migrated IDs */
                fullLabel = (d.isMoved ? "→ " : "") + (d.neighborType || d.key || "Group");
                const ids = d.entityIds && d.entityIds.length
                    ? d.entityIds
                    : (d.entitySubgroups || []).flatMap(sg => sg.entityIds || []).map(String).filter(Boolean);
                lines.push(`${ids.length} ids`);
                lines.push(`${conns} connection${conns !== 1 ? "s" : ""}`);
                if (d.connectingIdCount) lines.push(`${d.connectingIdCount} connecting`);
                if (d.clusterEdgeCount) lines.push(`${d.clusterEdgeCount} cluster edge${d.clusterEdgeCount !== 1 ? "s" : ""}`);
                if (d.mergePreviewNew) lines.push("new from cluster 2");
                lines.push("click for full list");
            } else if (d.type === "entity_bundle") {
                fullLabel = this.labels.entity_bundle || "Collapsed entities";
                lines.push(`${d.bundleTotal ?? "—"} total`);
                lines.push(`${d.bundleDisplayed ?? "—"} on graph`);
                lines.push(`${d.bundleOverflow ?? "—"} collapsed`);
                if (d.connectingIdCount) lines.push(`${d.connectingIdCount} connecting`);
                if (d.clusterEdgeCount) lines.push(`${d.clusterEdgeCount} cluster edge${d.clusterEdgeCount !== 1 ? "s" : ""}`);
                lines.push("click to expand");
            } else if (d.type === "entity") {
                fullLabel = String(d.key || "Entity");
                lines.push(`${conns} connection${conns !== 1 ? "s" : ""}`);
                if (d.isConnecting) lines.push("connecting entity");
                if (d.clusterEdgeCount) lines.push(`${d.clusterEdgeCount} cluster edge${d.clusterEdgeCount !== 1 ? "s" : ""}`);
                if (d.inBothClusters) lines.push("in both clusters");
                if (d.mergePreviewNew) lines.push("new from cluster 2");
            } else if (d.type === "cluster") {
                const cnt = Number.isFinite(Number(d.hubConnectionCount)) ? Number(d.hubConnectionCount) : 0;
                if (d.isSplitNew) {
                    fullLabel = d.clusterFullId || d.key || `New Cluster ${Number(d.clusterIndex) - 1}`;
                    lines.push("drag nodes here to split");
                } else {
                    const idx = d.clusterIndex != null ? ` ${d.clusterIndex}` : "";
                    fullLabel = `Cluster${idx}${d.key ? ` · ${d.key}` : ""}`;
                }
                lines.push(`${cnt} node${cnt !== 1 ? "s" : ""} connected`);
            } else {
                fullLabel = this.labels[d.type] || d.type || "";
                if (d.key) lines.push(`key: ${d.key}`);
                lines.push(`${conns} connection${conns !== 1 ? "s" : ""}`);
                if (d.domain) lines.push(`domain: ${d.domain}`);
            }
            this.graphInspector = { visible: true, title: fullLabel, fullLabel, color, lines };
        },

        /* ── Float tooltip (non-curation) ────────────────────────────── */
        _showFloatTip(ev, d, container) {
            let tip = container.querySelector(".cgraph-float-tip");
            if (!tip) {
                tip = document.createElement("div");
                tip.className = "cgraph-float-tip";
                tip.style.cssText = "position:absolute;pointer-events:none;opacity:0;transition:opacity 0.15s;";
                container.appendChild(tip);
            }
            const conns = this._connCount(d);
            let extra = "";
            if (d.entityCount != null) extra += `<br>List size: ${d.entityCount}`;
            if (d.inBothClusters)      extra += `<br><em>In both clusters</em>`;
            if (d.neighborType)        extra += `<br>Type: <strong>${d.neighborType}</strong>`;
            if (d.neighborDomain)      extra += `<br>Domain: ${d.neighborDomain}`;
            tip.innerHTML = `<strong>${this.labels[d.type] || d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}<br>Connections: ${conns}${extra}`;
            tip.style.opacity = "1";
            const rect = container.getBoundingClientRect();
            tip.style.left = `${ev.clientX - rect.left + 16}px`;
            tip.style.top  = `${ev.clientY - rect.top  - 12}px`;
        },

        /* ── Highlight ────────────────────────────────────────────────── */
        _connCount(d) {
            return this.graphEdges.filter(e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                return s === d.id || t === d.id;
            }).length;
        },

        _highlightNeighbors(d, g) {
            const nb = new Set([d.id]);
            this.graphEdges.forEach(e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                if (s === d.id) nb.add(t);
                if (t === d.id) nb.add(s);
            });
            const dimN = (this.curationGraph && d.type === "cluster") ? 0.18 : 0.1;
            const dimL = dimN * 0.6;
            g.selectAll(".cgn").style("opacity", n => nb.has(n.id) ? 1 : dimN);
            g.selectAll(".cge")
                .style("opacity", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return s === d.id || t === d.id ? 0.95 : dimL;
                })
                .style("stroke", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return s === d.id || t === d.id ? "#f59e0b" : this.linkStroke(e);
                })
                .style("stroke-width", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return s === d.id || t === d.id ? 3 : this.linkWidth(e);
                });
        },

        _clearHighlight(g) {
            if (!g) { if (!this.svg) return; g = this.svg.select("g"); }
            g.selectAll(".cgn").style("opacity", 1);
            g.selectAll(".cge")
                .style("opacity", null)
                .style("stroke", e => this.linkStroke(e))
                .style("stroke-width", e => this.linkWidth(e));
        },

        /* ── Legend / filter ──────────────────────────────────────────── */
        _legendKey(d) {
            if (this.curationGraph && d.entityLegendKey) return d.entityLegendKey;
            return d.type;
        },

        toggleType(type) {
            if (type.startsWith("__")) return;
            if (this.hiddenTypes.has(type)) this.hiddenTypes.delete(type);
            else this.hiddenTypes.add(type);
            if (!this.svg) return;
            const g = this.svg.select("g");
            g.selectAll(".cgn").style("display", d => this.hiddenTypes.has(this._legendKey(d)) ? "none" : null);
            g.selectAll(".cge").style("display", e => {
                const s = typeof e.source === "object" ? e.source : { type: "" };
                const t = typeof e.target === "object" ? e.target : { type: "" };
                return this.hiddenTypes.has(this._legendKey(s)) || this.hiddenTypes.has(this._legendKey(t)) ? "none" : null;
            });
        },

        /* ── Zoom / view ──────────────────────────────────────────────── */
        zoomIn()  { if (this.svg && this.zoomBehavior) this.svg.transition().duration(280).call(this.zoomBehavior.scaleBy, 1.3); },
        zoomOut() { if (this.svg && this.zoomBehavior) this.svg.transition().duration(280).call(this.zoomBehavior.scaleBy, 0.77); },

        fitToScreen() {
            if (!this.svg || !this.zoomBehavior || !this.$refs.graphContainer) return;
            const c = this.$refs.graphContainer;
            const W = c.clientWidth, H = c.clientHeight || 600;
            let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
            this.graphNodes.forEach(n => {
                if (n.x != null && n.y != null) {
                    x0 = Math.min(x0, n.x); x1 = Math.max(x1, n.x);
                    y0 = Math.min(y0, n.y); y1 = Math.max(y1, n.y);
                }
            });
            if (!isFinite(x0)) return;
            const pad = 110;
            const gW = (x1 - x0) || 1, gH = (y1 - y0) || 1;
            const scale = Math.min(W / (gW + 2 * pad), H / (gH + 2 * pad), 0.92);
            const tx = W / 2 - scale * (x0 + gW / 2);
            const ty = H / 2 - scale * (y0 + gH / 2);
            this.svg.transition().duration(500).call(
                this.zoomBehavior.transform,
                d3.zoomIdentity.translate(tx, ty).scale(scale)
            );
        },

        resetView() {
            this._clearInspTimer();
            this.graphInspector = { visible: false, title: "", fullLabel: "", color: "#64748b", lines: [] };
            this._clearHighlight();
            this.hiddenTypes = new Set();
            if (this.svg) {
                this.svg.select("g").selectAll(".cgn").style("display", null);
                this.svg.select("g").selectAll(".cge").style("display", null);
            }
            this.detailNode = null;
            this.fitToScreen();
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
            this.$nextTick(() => {
                if (this.svg) {
                    const c = this.$refs.graphContainer;
                    this.svg.attr("viewBox", `0 0 ${c.clientWidth} ${c.clientHeight || 600}`);
                }
                this.fitToScreen();
            });
        },

        /* ── Stats / legend population ────────────────────────────────── */
        _entityGroupIdCount(d) {
            if (!d || d.type !== "entity_group") return 0;
            const n = Number(d.idCountInNode);
            if (Number.isFinite(n) && n >= 0) return n;
            if (d.entityIds && d.entityIds.length) return d.entityIds.length;
            return (d.entitySubgroups || []).reduce((acc, s) => acc + ((s.entityIds && s.entityIds.length) || 0), 0);
        },

        updateStats() {
            const shared  = this.graphNodes.filter(n => n.inBothClusters).length;
            const collapsed = this.graphNodes.filter(n => n.type === "entity_bundle")
                .reduce((sum, n) => sum + (Number(n.bundleOverflow) || 0), 0);
            this.graphStats = { nodes: this.graphNodes.length, edges: this.graphEdges.length, sharedEntities: shared, collapsedEntities: collapsed };

            const tc = {};
            this.graphNodes.forEach(n => { const k = this._legendKey(n); tc[k] = (tc[k] || 0) + 1; });
            const sorted = Object.entries(tc).sort((a, b) => b[1] - a[1]);
            this.legendItems = sorted.map(([type, count]) => {
                if (type.startsWith("entity:")) {
                    const nt = type.slice(7);
                    if (nt.length) {
                        /* Use full "type~domain" key so legend chip color matches the node color */
                        const fill = nt === "user_composite"
                            ? (this.colors["entity:user_composite"] || this.getColor("entity_group"))
                            : (this.prefixColorMap.get(nt.toLowerCase())
                                || this.getColor("entity_group"));
                        const label = nt === "user_composite"
                            ? "Custom composite"
                            : (this.curationGraph ? this.shortPairLabel(nt, 30) : `${nt} (entity)`);
                        /* The chip is shortened the same way node labels are, so the full key has
                           to stay reachable somewhere — it goes on the chip's tooltip. */
                        return { type, label, full: nt, color: fill, count, legendOverflow: false };
                    }
                }
                const dynColor = this.prefixColorMap.get(type) || this.getColor(type) || "#64748b";
                return { type, label: this.labels[type] || type, color: dynColor, count, legendOverflow: false };
            });
            this.legendExpanded = false;
        },

        /* ── Public util ──────────────────────────────────────────────── */
        downloadTextFile(filename, text) {
            const blob = new Blob([text == null ? "" : String(text)], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = Object.assign(document.createElement("a"), { href: url, download: filename, rel: "noopener" });
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },
    },
};
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────────────────── */
.cgraph-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    border-radius: inherit;
}
.cgraph-fullscreen {
    position: fixed !important;
    inset: 0;
    z-index: 1050;
    border-radius: 0 !important;
}
.cgraph-fullscreen .cgraph-canvas { flex: 1; min-height: unset; }

/* ── Toolbar ───────────────────────────────────────────────────────── */
.cgraph-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 12px;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    gap: 8px;
    flex-wrap: wrap;
}
.cgraph-tb-left  { display: flex; align-items: center; gap: 8px; }
.cgraph-tb-right { display: flex; align-items: center; gap: 4px; }
.cgraph-tb-sep   { width: 1px; height: 18px; background: #e2e8f0; margin: 0 4px; flex-shrink: 0; }

.cgraph-tb-btn {
    width: 26px; height: 26px;
    border: 1px solid #e2e8f0;
    border-radius: 7px;
    background: transparent;
    color: #64748b;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.cgraph-tb-btn:hover { background: #f1f5f9; color: #1e293b; border-color: #cbd5e1; }

.cgraph-stats { font-size: 11.5px; font-weight: 500; color: #475569; display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.cgraph-stats-sep     { color: #cbd5e1; }
.cgraph-stats-shared  { color: #3b82f6; font-weight: 600; }
.cgraph-stats-connecting { color: #0e89ab; font-weight: 600; }
.cgraph-stats-cluster-edges { color: #be185d; font-weight: 600; }
/* ── Canvas area wrapper (inspector floats here) ───────────────────── */
.cgraph-canvas-area {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.cgraph-lane-hdrs {
    position: absolute;
    top: 8px;
    left: 0;
    right: 0;
    z-index: 3;
    display: flex;
    justify-content: space-between;
    padding: 0 12px;
    pointer-events: none;
}
.cgraph-lane-hdr {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    max-width: 46%;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid var(--lane-color);
    background: #fff;
    background: color-mix(in srgb, var(--lane-color) 12%, #fff);
    font-size: 11px;
    font-weight: 700;
    color: var(--lane-color);
    pointer-events: auto;
}
.cgraph-lane-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--lane-color);
    flex-shrink: 0;
    align-self: center;
}
.cgraph-lane-name { white-space: nowrap; }
.cgraph-lane-id {
    font-family: monospace;
    font-size: 10px;
    font-weight: 500;
    opacity: 0.75;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
body.dark-mode .cgraph-lane-hdr {
    background: #1e293b;
    background: color-mix(in srgb, var(--lane-color) 22%, #0f172a);
    color: #e2e8f0;
}

/* ── Inspector bar — absolutely positioned, never shifts layout ─────── */
.cgraph-inspector {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(6px);
    border-bottom: 1px solid #bfdbfe;
    border-left: 3px solid #3b82f6;
    font-size: 12px;
    line-height: 1.5;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.07);
    pointer-events: none;
}
/* Chip: colored dot + full type~domain label */
.cgraph-insp-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: color-mix(in srgb, var(--ic, #3b82f6) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--ic, #3b82f6) 35%, transparent);
    border-radius: 20px;
    padding: 2px 10px 2px 5px;
    font-weight: 600;
    font-size: 12px;
    max-width: 320px;
    overflow: hidden;
    white-space: nowrap;
}
.cgraph-insp-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--ic, #3b82f6);
    flex-shrink: 0;
}
.cgraph-insp-label {
    overflow: hidden;
    text-overflow: ellipsis;
    color: color-mix(in srgb, var(--ic, #1e3a5f) 80%, #000);
}
.cgraph-insp-meta  { color: #475569; font-size: 12px; }
.cgraph-inspector-sep { margin: 0 4px; opacity: 0.5; }

/* Slide-down transition */
.cgraph-insp-enter-active, .cgraph-insp-leave-active { transition: transform 0.15s ease, opacity 0.15s ease; }
.cgraph-insp-enter-from, .cgraph-insp-leave-to { transform: translateY(-100%); opacity: 0; }

/* ── Legend ────────────────────────────────────────────────────────── */
.cgraph-legend {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: thin;
    gap: 4px;
    padding: 5px 12px;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}
/* Scanning mode: wrap, but never past a third of a short viewport. */
.cgraph-legend.is-wrapped {
    flex-wrap: wrap;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 5.5rem;
}
.cgraph-legend::-webkit-scrollbar { height: 6px; }
.cgraph-legend::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
.cgraph-legend-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 9px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: #fff;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    font-size: 11.5px;
    color: #334155;
    transition: all 0.14s;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.cgraph-legend-chip:hover { border-color: #94a3b8; box-shadow: 0 2px 6px rgba(0,0,0,0.09); transform: translateY(-1px); }
.cgraph-legend-chip.is-off { opacity: 0.38; background: #f8fafc; box-shadow: none; transform: none; }
/* Pinned to the right edge so the toggle stays reachable while the row scrolls. */
.cgraph-legend-more {
    border-style: dashed;
    color: #64748b;
    font-size: 11px;
    position: sticky;
    right: 0;
    background: #fff;
    box-shadow: -6px 0 6px -4px rgba(15, 23, 42, 0.12);
}
.cgraph-legend.is-wrapped .cgraph-legend-more { position: static; box-shadow: none; }
.cgraph-legend-dot  { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; box-shadow: 0 1px 3px rgba(0,0,0,0.22); }
.cgraph-legend-label { font-weight: 500; }
.cgraph-legend-cnt   { color: #94a3b8; font-size: 11px; }

/* ── Canvas ────────────────────────────────────────────────────────── */
.cgraph-canvas {
    position: relative;
    flex: 1;
    width: 100%;
    min-height: 0;
    cursor: grab;
    background-color: #f8fafc;
    background-image: radial-gradient(circle, #d1d5db 1px, transparent 1px);
    background-size: 28px 28px;
}
.cgraph-canvas:active { cursor: grabbing; }

/* ── Detail panel ──────────────────────────────────────────────────── */
.cgraph-panel {
    position: absolute;
    top: 108px; right: 14px;
    width: 300px;
    max-height: calc(100% - 122px);
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 20px 56px rgba(15, 23, 42, 0.17), 0 4px 14px rgba(0,0,0,0.07);
    z-index: 30;
    overflow: hidden;
}
.cgraph-panel--wide { width: min(390px, 92vw); }

.cgraph-panel-hdr {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    flex-shrink: 0;
    background: linear-gradient(135deg, #152844 0%, #1d3461 100%);
    border-left: 4px solid var(--node-color, #3b82f6);
}
.cgraph-panel-hdr-inner  { display: flex; align-items: center; gap: 10px; min-width: 0; }
.cgraph-panel-icon-badge {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: rgba(255,255,255,0.18);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #fff;
    flex-shrink: 0;
}
.cgraph-panel-hdr-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cgraph-panel-close {
    background: rgba(255,255,255,0.14);
    border: none;
    border-radius: 6px;
    color: #fff;
    width: 26px; height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    flex-shrink: 0;
    transition: background 0.12s;
}
.cgraph-panel-close:hover { background: rgba(255,255,255,0.3); }

.cgraph-panel-body {
    padding: 14px 16px;
    font-size: 12.5px;
    line-height: 1.85;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
}

/* Panel content atoms */
.cgraph-panel-meta-row  { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.cgraph-type-pill {
    display: inline-block;
    padding: 2px 10px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
    color: #1e40af;
    font-family: ui-monospace, monospace;
    word-break: break-all;
}
.cgraph-cnt-badge { background: #f1f5f9; border-radius: 20px; padding: 2px 9px; font-size: 11.5px; font-weight: 600; color: #64748b; }
.cgraph-id-list { max-height: 260px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; margin-bottom: 6px; }
.cgraph-id-row  { padding: 5px 12px; font-family: ui-monospace, monospace; font-size: 11.5px; color: #334155; border-bottom: 1px solid #f1f5f9; word-break: break-all; }
.cgraph-id-row:last-child { border-bottom: none; }
.cgraph-edge-row   { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.cgraph-edge-from  { font-weight: 700; color: #be185d; }
.cgraph-edge-arrow { font-size: 9px; color: #be185d; opacity: 0.8; }
.cgraph-edge-pair  { font-family: inherit; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; }
.cgraph-edge-to    { flex: 1 1 auto; min-width: 0; }

.cgraph-empty-note { color: #94a3b8; font-size: 12px; margin: 0; }
.cgraph-panel-note  { color: #64748b; font-size: 12px; margin: 0 0 10px; }

.cgraph-stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 7px;
    margin-bottom: 12px;
}
.cgraph-stat-cell {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.cgraph-stat-lbl       { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; line-height: 1.2; }
.cgraph-stat-val       { font-size: 1.2rem; font-weight: 700; color: #1e293b; line-height: 1.15; font-variant-numeric: tabular-nums; }
.cgraph-stat-val--warn { color: #d97706; }
.cgraph-btn-row        { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.cgraph-section-lbl    { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; margin: 8px 0 5px; }
.cgraph-mono-val       { font-family: ui-monospace, monospace; font-size: 12px; color: #1e293b; word-break: break-all; background: #f1f5f9; border-radius: 6px; padding: 6px 10px; margin: 4px 0; }
.cgraph-flag-row       { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.cgraph-flag           { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; }
.cgraph-flag--amber    { background: #fef3c7; color: #92400e; }
.cgraph-flag--green    { background: #d1fae5; color: #065f46; }
.cgraph-kv             { display: flex; flex-direction: column; margin-bottom: 7px; }
.cgraph-kv-lbl         { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin-bottom: 1px; }
.cgraph-kv-val         { font-size: 13px; color: #1e293b; }

/* Float tooltip */
:deep(.cgraph-float-tip) {
    position: absolute;
    background: rgba(15, 23, 42, 0.95);
    color: #e2e8f0;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12.5px;
    line-height: 1.65;
    box-shadow: 0 8px 24px rgba(0,0,0,0.28);
    max-width: 300px;
    z-index: 20;
    border: 1px solid rgba(255,255,255,0.08);
    pointer-events: none;
}

/* ── Panel slide transition ────────────────────────────────────────── */
.cgraph-slide-enter-active { transition: transform 0.24s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease; }
.cgraph-slide-leave-active { transition: transform 0.18s ease, opacity 0.15s ease; }
.cgraph-slide-enter-from,
.cgraph-slide-leave-to     { transform: translateX(18px) scale(0.97); opacity: 0; }
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .cgraph-toolbar {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }
    .cgraph-tb-btn {
        color: $dark-text-muted;
        border-color: $dark-border-color;
    }
    .cgraph-tb-btn:hover {
        background: $dark-border-color;
        color: $dark-text-primary;
    }
    .cgraph-tb-sep { background: $dark-border-color; }
    .cgraph-stats  { color: $dark-text-muted; }
    .cgraph-stats-sep { color: $dark-border-color; }
    .cgraph-stats-connecting { color: #fcd34d; }
    .cgraph-stats-cluster-edges { color: #fb7185; }
    .cgraph-inspector {
        background: rgba(15, 23, 42, 0.97);
        border-bottom-color: #1e3a5f;
        border-left-color: #3b82f6;
    }
    .cgraph-insp-chip { color: #e2e8f0; }
    .cgraph-insp-meta { color: $dark-text-muted; }

    .cgraph-legend {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }
    .cgraph-legend-chip {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;
    }
    .cgraph-legend-chip:hover { border-color: #4a5568; }
    .cgraph-legend-more { background: $dark-elevated-bg; box-shadow: -6px 0 6px -4px rgba(0,0,0,0.5); }
    .cgraph-legend::-webkit-scrollbar-thumb { background: #4a5568; }
    .cgraph-legend-cnt { color: $dark-text-muted; }

    .cgraph-canvas {
        background-color: $dark-card-bg;
        background-image: radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
    }

    .cgraph-panel {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        box-shadow: 0 20px 56px rgba(0,0,0,0.65), 0 4px 14px rgba(0,0,0,0.45);
    }
    .cgraph-panel-hdr { background: linear-gradient(135deg, #0d1e35 0%, #132040 100%); }
    .cgraph-panel-body { color: $dark-text-primary; }

    .cgraph-type-pill { background: #1e3a5f; border-color: #2d4a6d; color: #93c5fd; }
    .cgraph-cnt-badge { background: $dark-border-color; color: $dark-text-muted; }
    .cgraph-id-list   { background: $dark-body-bg; border-color: $dark-border-color; }
    .cgraph-id-row    { color: $dark-text-primary; border-bottom-color: $dark-border-color; }
    .cgraph-edge-from, .cgraph-edge-arrow { color: #fb7185; }
    .cgraph-edge-pair { color: $dark-text-muted; }

    .cgraph-stat-cell { background: $dark-body-bg; border-color: $dark-border-color; }
    .cgraph-stat-lbl  { color: $dark-text-muted; }
    .cgraph-stat-val  { color: $dark-text-primary; }

    .cgraph-mono-val  { background: $dark-body-bg; color: $dark-text-primary; }
    .cgraph-kv-val    { color: $dark-text-primary; }
    .cgraph-section-lbl { color: $dark-text-muted; }

    .cgraph-flag--amber { background: #451a03; color: #fbbf24; }
    .cgraph-flag--green { background: #052e16; color: #34d399; }

    .cgraph-panel-close:hover { background: rgba(255,255,255,0.25); }
}
</style>
